import React, { useCallback, useEffect, useMemo, useState } from "react";
import RangeFilter from "../components/RangeFilter";
import AppShell from "../components/AppShell";
import { PlayerTag } from "../components/badges";
import AdviceButtons from "../components/AdviceButtons";
import OutlookPanel from "../components/OutlookPanel";
import { toast } from "sonner";
import {
    TrendUp,
    TrendDown,
    Bandaids,
    CaretDown,
    CaretUp,
    Warning,
    MagnifyingGlass,
    X,
} from "@phosphor-icons/react";
import { getMarket } from "../lib/api";
import { MarketWatch } from "../components/MarketWatch";
import { selectStyles } from "../lib/select-styles";
import Select from "react-select";


const POSITION_OPTIONS = ["GKP", "DEF", "MID", "FWD"].map((p) => ({ value: p, label: p }));
const PAGE_SIZE = 50;
const DIRECTION_TABS = [
    { id: "bandwagons", label: "Bandwagons", icon: TrendUp, color: "#9EE8B4" },
    { id: "sell_offs", label: "Sell-offs", icon: TrendDown, color: "#FF2A2A" },
    { id: "watch", label: "Hlídač", icon: Warning, color: "#FF8800" },
];

const AVAILABILITY_CHIP = {
    injured: { bg: "#FF2A2A", text: "#fff", label: "INJ" },
    suspended: { bg: "#FF8800", text: "#050505", label: "SUS" },
    doubtful: { bg: "#FFD600", text: "#050505", label: "DBT" },
    unavailable: { bg: "#A1A1AA", text: "#050505", label: "OUT" },
};

const FDR_COLOR = {
    1: "#9EE8B4",
    2: "#9DB7F5",
    3: "#FFF3C2",
    4: "#FF8800",
    5: "#FF2A2A",
};

export default function Market() {
    const [data, setData] = useState({ bandwagons: [], sell_offs: [], teams: [], gameweek: null });
    const [loading, setLoading] = useState(true);
    const [direction, setDirection] = useState("bandwagons");

    // Filters
    const [query, setQuery] = useState("");
    const [positions, setPositions] = useState([]);
    const [teams, setTeams] = useState([]);
    const [priceRange, setPriceRange] = useState([4.0, 15.0]);
    const [ownershipRange, setOwnershipRange] = useState([0, 100]);
    const [formMin, setFormMin] = useState(0);
    const [fdrMax, setFdrMax] = useState(5);
    const [availableOnly, setAvailableOnly] = useState(false);
    // Pagination
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    // Row expansion
    const [expandedId, setExpandedId] = useState(null);

    // Sort
    const [sortKey, setSortKey] = useState("delta");
    const [sortDir, setSortDir] = useState("desc");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await getMarket(200);
            setData(d);
        } catch (e) {
            toast.error("Failed to load market data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const teamOptions = useMemo(
        () => data.teams.map((t) => ({ value: t.short, label: t.short })),
        [data.teams],
    );

    const raw = direction === "bandwagons" ? data.bandwagons : data.sell_offs;

    const filtered = useMemo(() => {
        const posSet = new Set(positions.map((p) => p.value));
        const teamSet = new Set(teams.map((t) => t.value));
        const q = query.trim().toLowerCase();
        return raw.filter((p) => {
            if (q && !((p.name || "").toLowerCase().includes(q))) return false;
            if (posSet.size && !posSet.has(p.position)) return false;
            if (teamSet.size && !teamSet.has(p.team)) return false;
            if (p.price < priceRange[0] || p.price > priceRange[1]) return false;
            if (p.ownership < ownershipRange[0] || p.ownership > ownershipRange[1]) return false;
            if (p.form < formMin) return false;
            const bestFdr = Math.min(...(p.next_fixtures.map((f) => f.fdr) || [6]));
            if (bestFdr > fdrMax) return false;
            if (availableOnly && p.availability?.level !== "available") return false;
            return true;
        });
    }, [raw, query, positions, teams, priceRange, ownershipRange, formMin, fdrMax, availableOnly]);

    // Reset pagination whenever filters/tab change
    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        setExpandedId(null);
    }, [direction, query, positions, teams, priceRange, ownershipRange, formMin, fdrMax, availableOnly]);

    const sorted = useMemo(() => {
        const list = [...filtered];
        list.sort((a, b) => {
            const av = getSortValue(a, sortKey);
            const bv = getSortValue(b, sortKey);
            if (av === bv) return 0;
            const cmp = av > bv ? 1 : -1;
            return sortDir === "asc" ? cmp : -cmp;
        });
        return list;
    }, [filtered, sortKey, sortDir]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("desc");
        }
    };

    const resetFilters = () => {
        setQuery("");
        setPositions([]);
        setTeams([]);
        setPriceRange([4.0, 15.0]);
        setOwnershipRange([0, 100]);
        setFormMin(0);
        setFdrMax(5);
        setAvailableOnly(false);
    };

    const visible = sorted.slice(0, visibleCount);
    const hasMore = sorted.length > visibleCount;

    return (
        <AppShell liveContext={data.gameweek ? { gameweek: data.gameweek } : null}>
            <div className="space-y-6">
                <header className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter">
                            Transfer Market
                        </h1>
                        <p className="text-white/60 text-sm mt-1">
                            {raw.length} players moving · GW{data.gameweek} · search, filter, expand for multi-window outlook.
                        </p>
                    </div>
                    <div className="flex gap-1" data-testid="market-direction-tabs">
                        {DIRECTION_TABS.map((t) => {
                            const Icon = t.icon;
                            const active = direction === t.id;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => setDirection(t.id)}
                                    className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${
                                        active
                                            ? "border-fa-yellow text-fa-yellow bg-fa-yellow/10"
                                            : "border-white/10 text-white/70 hover:border-white/30"
                                    }`}
                                    data-testid={`market-tab-${t.id}`}
                                >
                                    <Icon size={14} weight="bold" color={t.color} />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </header>

                {direction === "watch" ? (
                    <MarketWatch />
                ) : (
                <>
                <SearchBar query={query} onChange={setQuery} />

                <FilterPanel
                    positions={positions}
                    setPositions={setPositions}
                    teams={teams}
                    setTeams={setTeams}
                    teamOptions={teamOptions}
                    priceRange={priceRange}
                    setPriceRange={setPriceRange}
                    ownershipRange={ownershipRange}
                    setOwnershipRange={setOwnershipRange}
                    formMin={formMin}
                    setFormMin={setFormMin}
                    fdrMax={fdrMax}
                    setFdrMax={setFdrMax}
                    availableOnly={availableOnly}
                    setAvailableOnly={setAvailableOnly}
                    onReset={resetFilters}
                />

                <StatsStrip total={raw.length} visible={sorted.length} direction={direction} />

                {loading ? (
                    <div className="text-white/40 font-mono text-sm">Loading market…</div>
                ) : sorted.length === 0 ? (
                    <div className="border border-dashed border-white/10 p-10 text-center text-white/50 font-mono text-xs uppercase">
                        No players match these filters. Try loosening the range.
                    </div>
                ) : (
                    <>
                        <MarketTable
                            players={visible}
                            direction={direction}
                            sortKey={sortKey}
                            sortDir={sortDir}
                            onSort={toggleSort}
                            expandedId={expandedId}
                            setExpandedId={setExpandedId}
                        />
                        {hasMore && (
                            <div className="flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                                    data-testid="market-load-more"
                                    className="font-mono text-xs uppercase tracking-widest px-4 py-2 border border-white/20 hover:border-fa-yellow hover:text-fa-yellow transition-colors"
                                >
                                    Load {Math.min(PAGE_SIZE, sorted.length - visibleCount)} more
                                    <span className="ml-2 text-white/40">({visibleCount} of {sorted.length})</span>
                                </button>
                            </div>
                        )}
                    </>
                )}
                </>
                )}
            </div>
        </AppShell>
    );
}

function SearchBar({ query, onChange }) {
    return (
        <div className="relative" data-testid="market-search-bar">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
                type="text"
                placeholder="Search by player name…"
                value={query}
                onChange={(e) => onChange(e.target.value)}
                data-testid="market-search-input"
                className="w-full bg-[#121212] border border-white/10 focus:border-fa-yellow/60 outline-none text-white font-mono text-sm px-10 py-2.5"
            />
            {query && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    data-testid="market-search-clear"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-fa-yellow"
                >
                    <X size={14} weight="bold" />
                </button>
            )}
        </div>
    );
}

function getSortValue(p, key) {
    if (key === "delta") return p.delta;
    if (key === "ownership") return p.ownership;
    if (key === "price") return p.price;
    if (key === "form") return p.form;
    if (key === "fdr") return Math.min(...(p.next_fixtures.map((f) => f.fdr) || [6]));
    if (key === "name") return (p.name || "").toLowerCase();
    if (key === "team") return p.team || "";
    if (key === "points") return p.total_points;
    return p.delta;
}

function StatsStrip({ total, visible, direction }) {
    const color = direction === "bandwagons" ? "#9EE8B4" : "#FF2A2A";
    return (
        <div className="bg-[#121212] border border-white/10 px-4 py-3 flex flex-wrap items-center gap-6">
            <Stat label="Total" value={total} />
            <Stat label="Visible" value={visible} color={color} />
            <Stat label="Side" value={direction === "bandwagons" ? "IN" : "OUT"} color={color} />
        </div>
    );
}

function Stat({ label, value, color }) {
    return (
        <div className="flex flex-col">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/40 leading-none">
                {label}
            </span>
            <span
                className="font-display font-black uppercase tracking-tight text-lg leading-none mt-1"
                style={{ color: color || "#ffffff" }}
            >
                {value}
            </span>
        </div>
    );
}

function FilterPanel(props) {
    const {
        positions, setPositions, teams, setTeams, teamOptions,
        priceRange, setPriceRange, ownershipRange, setOwnershipRange,
        formMin, setFormMin, fdrMax, setFdrMax,
        availableOnly, setAvailableOnly, onReset,
    } = props;

    return (
        <div className="bg-[#121212] border border-white/10 p-4" data-testid="market-filters">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <FilterBlock label="Position">
                    <Select
                        isMulti
                        options={POSITION_OPTIONS}
                        value={positions}
                        onChange={(v) => setPositions(v || [])}
                        styles={selectStyles}
                        placeholder="Any"
                    />
                </FilterBlock>
                <FilterBlock label="Team">
                    <Select
                        isMulti
                        options={teamOptions}
                        value={teams}
                        onChange={(v) => setTeams(v || [])}
                        styles={selectStyles}
                        placeholder="Any"
                    />
                </FilterBlock>
                <div className="md:col-span-2 space-y-2">
                    <RangeFilter
                        id="price"
                        label="Price"
                        minValue={4.0}
                        maxValue={15.0}
                        selectedMin={priceRange[0]}
                        selectedMax={priceRange[1]}
                        step={0.5}
                        unit="£m"
                        format="currency"
                        onChange={({ selectedMin, selectedMax }) => setPriceRange([selectedMin, selectedMax])}
                        defaults={{ selectedMin: 4.0, selectedMax: 15.0 }}
                        testid="range-price"
                    />
                    <RangeFilter
                        id="ownership"
                        label="Ownership"
                        minValue={0}
                        maxValue={100}
                        selectedMin={ownershipRange[0]}
                        selectedMax={ownershipRange[1]}
                        step={1}
                        unit="%"
                        format="percent"
                        onChange={({ selectedMin, selectedMax }) => setOwnershipRange([selectedMin, selectedMax])}
                        defaults={{ selectedMin: 0, selectedMax: 100 }}
                        testid="range-ownership"
                    />
                </div>
                <div className="md:col-span-2 space-y-2">
                    <RangeFilter
                        id="form"
                        label="Form"
                        minValue={0}
                        maxValue={10}
                        selectedMin={formMin}
                        selectedMax={10}
                        step={0.5}
                        format="number"
                        onChange={({ selectedMin }) => setFormMin(selectedMin)}
                        defaults={{ selectedMin: 0, selectedMax: 10 }}
                        testid="range-form"
                    />
                    <FilterBlock label={`Best FDR ≤ ${fdrMax}`}>
                        <SingleSlider min={1} max={5} step={1} value={fdrMax} onChange={setFdrMax} />
                    </FilterBlock>
                </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
                <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/70 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={availableOnly}
                        onChange={(e) => setAvailableOnly(e.target.checked)}
                        className="accent-fa-yellow"
                        data-testid="available-only-toggle"
                    />
                    Available players only
                </label>
                <button
                    onClick={onReset}
                    className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-fa-yellow transition-colors"
                    data-testid="reset-filters-btn"
                >
                    Reset filters
                </button>
            </div>
        </div>
    );
}

function FilterBlock({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">
                {label}
            </div>
            {children}
        </div>
    );
}

function SingleSlider({ min, max, step, value, onChange }) {
    return (
        <div className="h-9 flex items-center">
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="flex-1 accent-fa-yellow"
            />
        </div>
    );
}

function MarketTable({ players, direction, sortKey, sortDir, onSort, expandedId, setExpandedId }) {
    const deltaColor = direction === "bandwagons" ? "#9EE8B4" : "#FF2A2A";
    const cols = [
        { key: "name", label: "Player" },
        { key: "team", label: "Team" },
        { key: "position", label: "Pos" },
        { key: "price", label: "Price" },
        { key: "ownership", label: "Own %" },
        { key: "form", label: "Form" },
        { key: "delta", label: direction === "bandwagons" ? "In" : "Out" },
        { key: "fdr", label: "Next 3" },
        { key: "points", label: "Pts" },
    ];

    return (
        <div className="bg-[#121212] border border-white/10 overflow-x-auto" data-testid="market-table">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-white/10 text-left">
                        {cols.map((c) => (
                            <th
                                key={c.key}
                                onClick={() => onSort(c.key)}
                                className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/50 cursor-pointer hover:text-fa-yellow transition-colors select-none"
                                data-testid={`sort-${c.key}`}
                            >
                                <span className="flex items-center gap-1">
                                    {c.label}
                                    {sortKey === c.key && (
                                        sortDir === "asc"
                                            ? <CaretUp size={10} weight="bold" />
                                            : <CaretDown size={10} weight="bold" />
                                    )}
                                </span>
                            </th>
                        ))}
                        <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/50">
                            Community
                        </th>
                        <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/50">
                            Tags
                        </th>
                        <th className="px-3 py-2" />
                    </tr>
                </thead>
                <tbody>
                    {players.map((p) => (
                        <React.Fragment key={p.id}>
                            <MarketRow
                                p={p}
                                deltaColor={deltaColor}
                                expanded={expandedId === p.id}
                                onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
                            />
                            {expandedId === p.id && (
                                <tr className="bg-black/40 border-b border-white/10">
                                    <td colSpan={cols.length + 3} className="p-4">
                                        <OutlookPanel playerId={p.id} />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function MarketRow({ p, deltaColor, expanded, onToggle }) {
    const avail = p.availability || {};
    const isUnavailable = avail.level && avail.level !== "available";
    const availChip = AVAILABILITY_CHIP[avail.level];
    const deltaK = Math.round(Math.abs(p.delta) / 1000);
    return (
        <tr
            className={`border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${
                isUnavailable ? "bg-fa-red/5" : ""
            } ${expanded ? "bg-white/[0.04]" : ""}`}
            data-testid={`market-row-${p.id}`}
            onClick={onToggle}
        >
            <td className="px-3 py-2.5">
                <div className="flex items-center gap-2">
                    <span className="font-display font-bold uppercase">{p.name}</span>
                    {availChip && (
                        <span
                            className="font-tag text-[9px] font-bold uppercase px-1 py-0.5 leading-none flex items-center gap-1"
                            style={{ background: availChip.bg, color: availChip.text }}
                            title={avail.news || avail.label}
                            data-testid={`market-avail-${p.id}`}
                        >
                            <Bandaids size={9} weight="fill" />
                            {availChip.label}
                            {avail.level === "doubtful" && avail.chance != null && ` ${avail.chance}%`}
                        </span>
                    )}
                </div>
                {isUnavailable && avail.news && (
                    <div className="font-mono text-[9px] text-fa-red/70 mt-0.5 max-w-[300px] truncate" title={avail.news}>
                        <Warning size={9} weight="fill" className="inline mr-1" />
                        {avail.news}
                    </div>
                )}
            </td>
            <td className="px-3 py-2.5 font-mono text-xs text-white/80">{p.team}</td>
            <td className="px-3 py-2.5">
                <span className="font-tag text-[10px] font-bold uppercase px-1.5 py-0.5 leading-none bg-white/10">
                    {p.position}
                </span>
            </td>
            <td className="px-3 py-2.5 font-mono text-xs text-fa-yellow tabular-nums">
                £{p.price.toFixed(1)}
            </td>
            <td className="px-3 py-2.5 font-mono text-xs tabular-nums">{p.ownership}%</td>
            <td className="px-3 py-2.5 font-mono text-xs tabular-nums">{p.form.toFixed(1)}</td>
            <td
                className="px-3 py-2.5 font-mono text-xs font-bold tabular-nums"
                style={{ color: deltaColor }}
            >
                {p.delta > 0 ? "+" : "-"}
                {deltaK}k
            </td>
            <td className="px-3 py-2.5">
                <div className="flex items-center gap-1">
                    {(p.next_fixtures || []).map((f) => (
                        <FixtureChip key={`${f.gw}-${f.opponent}`} f={f} />
                    ))}
                </div>
            </td>
            <td className="px-3 py-2.5 font-mono text-xs tabular-nums text-white/70">
                {p.total_points}
            </td>
            <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                <AdviceButtons
                    playerId={p.id}
                    compact
                    advice={p.community_advice}
                />
            </td>
            <td className="px-3 py-2.5">
                <div className="flex flex-wrap gap-1">
                    {p.tags.map((t) => (
                        <PlayerTag key={t} tag={t} />
                    ))}
                </div>
            </td>
            <td className="px-3 py-2.5 text-white/40">
                {expanded ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
            </td>
        </tr>
    );
}

function FixtureChip({ f }) {
    const color = FDR_COLOR[f.fdr] || "#888";
    return (
        <span
            className="font-mono text-[9px] font-bold uppercase px-1 py-0.5 leading-none border"
            style={{ color, borderColor: `${color}60`, background: `${color}10` }}
            title={`GW${f.gw} · ${f.home_away === "H" ? "vs" : "@"} ${f.opponent} (FDR ${f.fdr})`}
        >
            {f.home_away === "H" ? "" : "@"}
            {f.opponent}
            <span className="ml-0.5 text-white/40">·{f.fdr}</span>
        </span>
    );
}
